import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import { PageHeader } from '../components/PageHeader'
import {
  Button,
  Card,
  Chip,
  EmptyState,
  Input,
  Modal,
  ProviderTag,
  Skeleton,
} from '../components/ui'
import { Table, TBody, TH, THead } from '../components/ui'
import { useEventBookmarks, useInfiniteEvents } from '../api/hooks'
import type { AccessEvent, EventBookmark, EventsParams, Severity, SystemProvider } from '../api/types'
import { EventTableRow, eventTitle, typeLabel, useEventPresentation, vendorSummary } from '../components/features/EventRows'
import { LocationLink } from '../components/LocationLink'

const SEVERITIES: { value: Severity | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'info', label: 'Info' },
  { value: 'notice', label: 'Notice' },
  { value: 'warning', label: 'Warning' },
  { value: 'critical', label: 'Critical' },
]

const SINCE_CHOICES: { hours: number | null; label: string }[] = [
  { hours: 1, label: '1h' },
  { hours: 24, label: '24h' },
  { hours: 168, label: '7d' },
  { hours: 720, label: '30d' },
  { hours: null, label: 'All time' },
]

/** Common event keys for the type datalist (server accepts any prefix string). */
const TYPE_SUGGESTIONS = [
  'access.granted',
  'access.denied',
  'intercom.call',
  'intercom.call_missed',
  'system.online',
  'system.offline',
  'system.auth_failed',
  'credential.created',
  'credential.blocked',
]

function FilterChip({
  active,
  label,
  onClick,
}: {
  active: boolean
  label: string
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-sm border px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wider transition-colors focus:outline-none focus:ring-2 focus:ring-ostrel-gold ${
        active
          ? 'border-ostrel-navy bg-ostrel-navy text-ostrel-light'
          : 'border-ostrel-line bg-ostrel-card text-ostrel-ink-muted hover:bg-ostrel-sand'
      }`}
    >
      {label}
    </button>
  )
}

const bookmarkChip: Record<EventBookmark['status'], { tone: 'ok' | 'danger' | 'muted'; label: string }> = {
  ok: { tone: 'ok', label: 'bookmarked' },
  failed: { tone: 'danger', label: 'failed' },
  dry_run: { tone: 'muted', label: 'dry run' },
}

/** Wisenet WAVE camera bookmarks made for this event — nothing at all when
 *  there are none (most events, and every customer without WAVE); a small
 *  muted error line if the lookup itself failed. */
export function EventBookmarksRow({ eventUid }: { eventUid: string }) {
  const bookmarks = useEventBookmarks(eventUid)
  if (bookmarks.isError) {
    return (
      <div className="flex items-baseline justify-between gap-4 py-1.5">
        <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          Bookmarks
        </dt>
        <dd className="text-xs text-ostrel-ink-muted">Could not load bookmarks.</dd>
      </div>
    )
  }
  const rows = bookmarks.data ?? []
  if (rows.length === 0) return null
  return (
    <div className="flex items-baseline justify-between gap-4 py-1.5">
      <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
        Bookmarks
      </dt>
      <dd className="flex min-w-0 flex-col items-end gap-1 text-xs text-ostrel-ink">
        <span className="tnum">
          Bookmarked on {rows.length} {rows.length === 1 ? 'camera' : 'cameras'}
        </span>
        <ul className="flex max-w-full flex-col items-end gap-1">
          {rows.map((b) => {
            const chip = bookmarkChip[b.status] ?? { tone: 'muted' as const, label: b.status }
            return (
              <li key={b.camera_uid} className="flex max-w-full items-center gap-2">
                <Link to={`/devices/${b.camera_uid}`} className="min-w-0 truncate hover:text-ostrel-accent">
                  {b.camera_name}
                </Link>
                <span title={b.status === 'failed' && b.last_error ? b.last_error : undefined}>
                  <Chip tone={chip.tone} label={chip.label} />
                </span>
              </li>
            )
          })}
        </ul>
      </dd>
    </div>
  )
}

export function EventDetailModal({
  event,
  personName,
  locationName,
  deviceName,
  zoneName,
  systemProvider,
  systemName,
  onClose,
  compact = false,
}: {
  event: AccessEvent | null
  personName: (uid: string | null) => string | null
  locationName: (uid: string | null) => string | null
  deviceName: (uid: string | null) => string | null
  zoneName: (uid: string | null) => string | null
  systemProvider: (uid: string | null) => SystemProvider | null
  systemName: (uid: string | null) => string | null
  onClose: () => void
  /** Dashboard variant: the glanceable field set only — no vendor payload
   *  breakdown, no dedupe key, no raw dump. */
  compact?: boolean
}) {
  if (!event) return null
  const vendor = vendorSummary(event)
  // Where the event came from: the provider tag + the connection's name.
  const provider = systemProvider(event.system)
  const source: ReactNode = event.system ? (
    <span className="inline-flex items-center gap-1.5">
      {provider && <ProviderTag provider={provider} />}
      {systemName(event.system) ?? event.system}
    </span>
  ) : null
  const rows: { label: string; value: ReactNode }[] = compact
    ? [
        { label: 'Occurred at', value: new Date(event.occurred_at).toLocaleString() },
        { label: 'Type', value: event.type },
        { label: 'Severity', value: event.severity },
        { label: 'Source', value: source },
        { label: 'Event', value: vendor.event },
        { label: 'Person', value: personName(event.person) ?? event.person },
        { label: 'Credential', value: event.credential },
        { label: 'Device', value: deviceName(event.device) ?? event.device },
        { label: 'Location', value: locationName(event.location) ?? event.location },
      ]
    : [
        { label: 'Event id', value: event.unique_id },
        { label: 'Occurred at', value: new Date(event.occurred_at).toLocaleString() },
        { label: 'Type', value: event.type },
        { label: 'Severity', value: event.severity },
        { label: 'Event', value: vendor.event },
        { label: 'Place', value: vendor.place },
        { label: 'Subject', value: vendor.subject },
        { label: 'Source', value: source },
        { label: 'Person', value: personName(event.person) ?? event.person },
        { label: 'Credential', value: event.credential },
        { label: 'Device', value: deviceName(event.device) ?? event.device },
        { label: 'Location', value: locationName(event.location) ?? event.location },
        { label: 'Zone', value: zoneName(event.zone) ?? event.zone },
        { label: 'Dedupe key', value: event.dedupe_key },
      ]
  return (
    <Modal open title={eventTitle(event)} onClose={onClose}>
      <div className="flex flex-col gap-4">
        <dl className="flex flex-col divide-y divide-ostrel-line">
          {rows.map((r) => (
            <div key={r.label} className="flex items-baseline justify-between gap-4 py-1.5">
              <dt className="shrink-0 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
                {r.label}
              </dt>
              <dd className="tnum truncate text-right text-xs text-ostrel-ink">
                {r.label === 'Location' && event.location ? (
                  <LocationLink uid={event.location}>{r.value}</LocationLink>
                ) : (
                  (r.value ?? '—')
                )}
              </dd>
            </div>
          ))}
          <EventBookmarksRow eventUid={event.unique_id} />
        </dl>
        {!compact && event.raw && Object.keys(event.raw).length > 0 && (
          <div className="flex flex-col gap-1.5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Raw payload
            </p>
            <pre className="tnum max-h-64 overflow-auto rounded-sm border border-ostrel-line bg-ostrel-sand p-3 font-mono text-[11px] leading-relaxed text-ostrel-ink">
              {JSON.stringify(event.raw, null, 2)}
            </pre>
          </div>
        )}
      </div>
    </Modal>
  )
}

export default function EventsPage() {
  const [severity, setSeverity] = useState<Severity | 'all'>('all')
  const [typeFilter, setTypeFilter] = useState('')
  const [sinceHours, setSinceHours] = useState<number | null>(24)
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<AccessEvent | null>(null)

  const params: EventsParams = useMemo(
    () => ({
      since: sinceHours === null ? undefined : new Date(Date.now() - sinceHours * 3600 * 1000).toISOString(),
      type: typeFilter.trim() || undefined,
      severity: severity === 'all' ? undefined : severity,
    }),
    [sinceHours, typeFilter, severity],
  )

  const events = useInfiniteEvents(params)
  // Shared row presentation (EventRows): resolvers + context line — the
  // search filter below matches on the same contextLine the rows show.
  const { contextLine, systemProviders, systemName, personName, locationName, deviceName, zoneName } =
    useEventPresentation()
  const systemProvider = (uid: string | null) => (uid ? (systemProviders.get(uid) ?? null) : null)

  const items = useMemo(() => events.data?.pages.flatMap((p) => p.items) ?? [], [events.data])

  const needle = search.trim().toLowerCase()
  const visible = needle
    ? items.filter((e) =>
        [
          // What the row shows (names + vendor context) plus the raw refs.
          contextLine(e),
          typeLabel(e.type),
          e.device,
          e.person,
          e.location,
          e.zone,
        ]
          .filter(Boolean)
          .some((v) => (v as string).toLowerCase().includes(needle)),
      )
    : items

  return (
    <div className="flex flex-col gap-6">
      <PageHeader section="Audit & telemetry" title="Events" />

      <Card>
        <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Severity
            </span>
            {SEVERITIES.map((s) => (
              <FilterChip
                key={s.value}
                label={s.label}
                active={severity === s.value}
                onClick={() => setSeverity(s.value)}
              />
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
              Since
            </span>
            {SINCE_CHOICES.map((c) => (
              <FilterChip
                key={c.label}
                label={c.label}
                active={sinceHours === c.hours}
                onClick={() => setSinceHours(c.hours)}
              />
            ))}
          </div>
          <div className="w-48">
            <Input
              aria-label="Event type filter"
              placeholder="Type, e.g. access.granted"
              list="event-type-suggestions"
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
            />
            <datalist id="event-type-suggestions">
              {TYPE_SUGGESTIONS.map((t) => (
                <option key={t} value={t} />
              ))}
            </datalist>
          </div>
          <div className="w-56">
            <Input
              aria-label="Search loaded events"
              placeholder="Search person, location, device…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <span className="tnum ml-auto text-[11px] text-ostrel-ink-muted">
            {visible.length}
            {needle && ` of ${items.length}`} loaded
            {events.hasNextPage ? '+' : ''}
          </span>
        </div>
      </Card>

      <Card className="overflow-hidden">
        {events.isPending ? (
          <div className="flex flex-col gap-2">
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
            <Skeleton className="h-9" />
          </div>
        ) : events.isError ? (
          <EmptyState
            title="Could not load events"
            hint={events.error instanceof Error ? events.error.message : String(events.error)}
          />
        ) : visible.length === 0 ? (
          <EmptyState
            title={needle ? 'No matches in loaded events' : 'No events in this window'}
            hint={
              needle
                ? 'Try a different search, or load more pages first.'
                : 'Widen the time window or loosen the filters.'
            }
          />
        ) : (
          <>
            <Table>
              <THead>
                <tr>
                  <TH>Time</TH>
                  <TH>Event</TH>
                  <TH>System</TH>
                  <TH>Severity</TH>
                </tr>
              </THead>
              <TBody>
                {visible.map((e) => (
                  <EventTableRow key={e.unique_id} event={e} onSelect={setSelected} />
                ))}
              </TBody>
            </Table>
            {events.hasNextPage && (
              <div className="mt-4 flex justify-center">
                <Button
                  variant="ghost"
                  disabled={events.isFetchingNextPage}
                  onClick={() => events.fetchNextPage()}
                >
                  {events.isFetchingNextPage ? 'Loading…' : '← Load more'}
                </Button>
              </div>
            )}
          </>
        )}
      </Card>

      <EventDetailModal
        event={selected}
        personName={personName}
        locationName={locationName}
        deviceName={deviceName}
        zoneName={zoneName}
        systemProvider={systemProvider}
        systemName={systemName}
        onClose={() => setSelected(null)}
      />
    </div>
  )
}
